import { useQuery } from "@tanstack/react-query"
import type { ColumnDef } from "@tanstack/react-table"
import { Search, Users } from "lucide-react"
import * as React from "react"
import { useNavigate } from "react-router-dom"
import { type Candidate, candidatesApi } from "@/api/candidates"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { PageHeader } from "@/components/dashboard/page-header"
import { DataTable } from "@/components/shared/data-table"
import { EmptyState } from "@/components/shared/empty-state"
import { formatDate, initials } from "@/lib/utils"

export function CandidatesPage() {
  const [q, setQ] = React.useState("")
  const nav = useNavigate()
  const query = useQuery({
    queryKey: ["candidates", q],
    queryFn: () => candidatesApi.list(q || undefined),
  })

  const columns = React.useMemo<ColumnDef<Candidate>[]>(
    () => [
      {
        accessorKey: "full_name",
        header: "Aday",
        cell: ({ row }) => (
          <div className="flex items-center gap-3">
            <Avatar className="size-9">
              <AvatarFallback className="bg-primary-soft text-primary">
                {initials(row.original.full_name)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <div className="truncate font-medium">{row.original.full_name}</div>
              <div className="truncate text-xs text-muted-foreground">
                {row.original.email}
              </div>
            </div>
          </div>
        ),
      },
      {
        accessorKey: "location",
        header: "Konum",
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {row.original.location ?? "—"}
          </span>
        ),
      },
      {
        accessorKey: "tags",
        header: "Etiketler",
        cell: ({ row }) => {
          const tags = row.original.tags ?? []
          if (tags.length === 0) return <span className="text-xs text-muted-foreground">—</span>
          return (
            <div className="flex flex-wrap gap-1">
              {tags.slice(0, 3).map((t) => (
                <Badge key={t} variant="secondary" className="text-[10px]">
                  {t}
                </Badge>
              ))}
              {tags.length > 3 && (
                <Badge variant="outline" className="text-[10px]">
                  +{tags.length - 3}
                </Badge>
              )}
            </div>
          )
        },
        enableSorting: false,
      },
      {
        accessorKey: "source",
        header: "Kaynak",
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {row.original.source ?? "—"}
          </span>
        ),
      },
      {
        accessorKey: "created_at",
        header: "Eklendi",
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {formatDate(row.original.created_at)}
          </span>
        ),
      },
    ],
    [],
  )

  return (
    <>
      <PageHeader
        title="Adaylar"
        description="Tüm başvuru havuzu — arama, etiketleme ve detay için aday satırına tıkla."
      />
      <div className="space-y-4 p-4 sm:p-6">
        <div className="relative max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Ada veya e-postaya göre ara…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
          />
        </div>

        <DataTable
          columns={columns}
          data={query.data?.data ?? []}
          isLoading={query.isLoading}
          onRowClick={(c) => nav(`/dashboard/candidates/${c.id}`)}
          empty={
            <EmptyState
              icon={Users}
              title={q ? "Eşleşen aday yok" : "Henüz aday yok"}
              description={
                q
                  ? "Farklı bir isim veya e-posta deneyebilirsin."
                  : "Bir ilan yayınla; ilk başvurular buraya düşmeye başlasın."
              }
            />
          }
        />
      </div>
    </>
  )
}
